import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useStore } from '../../../../hooks/useStore';
import Popup from '../../../../components/common-components/pop-up/Popup';
import CommonButton from '../../../../components/common-components/button/CommonButton';
import { IExpenseResult, IExpenseFileResult } from '../../../../models/expense/ExpenseModel';
import { expenseService } from '../../../../services/modules/expense/expense.service';
import { Download, File as FileIcon, Coins } from 'lucide-react';
import { toast } from 'react-toastify';
import styles from './ExpenseDetailPopup.module.css';

const ExpenseDetailPopup = () => {
  const { popupStore, expenseStore } = useStore();
  const [expense, setExpense] = useState<IExpenseResult | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (popupStore.isExpenseDetailOpen && expenseStore.selectedExpense) {
      loadExpenseDetails();
    }
  }, [popupStore.isExpenseDetailOpen, expenseStore.selectedExpense]);

  const loadExpenseDetails = async () => {
    if (!expenseStore.selectedExpense?.id) return;

    setLoading(true);
    try {
      const expenseData = await expenseStore.getExpenseById(expenseStore.selectedExpense.id);
      if (expenseData) {
        setExpense(expenseData);
      }
    } catch (error) {
      console.error('Error loading expense details:', error);
      toast.error('Harcama detayları yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFile = async (fileUniqueId: string) => {
    try {
      await expenseService.downloadExpenseFile(fileUniqueId);
      toast.success('Dosya indirildi');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Dosya indirilirken bir hata oluştu');
    }
  };

  const handleClose = () => {
    popupStore.setIsExpenseDetailOpen(false);
    expenseStore.setSelectedExpense(null);
    setExpense(null);
  };

  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
    }).format(amount);
  };

  const formatDate = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'Beklemede';
      case 'Approved':
        return 'Onaylandı';
      case 'Rejected':
        return 'Reddedildi';
      default:
        return status;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'Pending':
        return styles.statusPending;
      case 'Approved':
        return styles.statusApproved;
      case 'Rejected':
        return styles.statusRejected;
      default:
        return '';
    }
  };

  if (!expenseStore.selectedExpense && !expense) return null;

  return (
    <Popup
      isOpen={popupStore.isExpenseDetailOpen}
      onClose={handleClose}
      title="Harcama Detayları"
      footer={
        <CommonButton type="cancel" onClick={handleClose}>
          Kapat
        </CommonButton>
      }
    >
      <div className={styles.container}>
        {loading ? (
          <div className={styles.loading}>Yükleniyor...</div>
        ) : expense ? (
          <>
            <div className={styles.detailSection}>
              <div className={styles.detailRow}>
                <span className={styles.label}>Miktar:</span>
                <span className={styles.value}>
                  <Coins size={16} className={styles.currencyIcon} />
                  {formatAmount(expense.amount)}
                </span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.label}>Harcama Tarihi:</span>
                <span className={styles.value}>{formatDate(expense.expenseDate)}</span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.label}>Açıklama:</span>
                <span className={styles.value}>{expense.description}</span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.label}>Durum:</span>
                <span className={`${styles.statusBadge} ${getStatusClass(expense.status)}`}>
                  {getStatusText(expense.status)}
                </span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.label}>Kullanıcı:</span>
                <span className={styles.value}>{expense.userName}</span>
              </div>

              <div className={styles.detailRow}>
                <span className={styles.label}>Oluşturulma Tarihi:</span>
                <span className={styles.value}>{formatDate(expense.createdDate)}</span>
              </div>
            </div>

            {expense.files && expense.files.length > 0 && (
              <div className={styles.filesSection}>
                <h3 className={styles.filesTitle}>Dosyalar</h3>
                <div className={styles.fileList}>
                  {expense.files.map((file: IExpenseFileResult) => (
                    <div key={file.id} className={styles.fileItem}>
                      <FileIcon size={16} className={styles.fileIcon} />
                      <span className={styles.fileName} title={file.fileName}>
                        {file.fileName}
                      </span>
                      <button
                        className={styles.downloadButton}
                        onClick={() => handleDownloadFile(file.uniqueId)}
                        title="İndir"
                      >
                        <Download size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className={styles.error}>Harcama bilgisi bulunamadı</div>
        )}
      </div>
    </Popup>
  );
};

export default observer(ExpenseDetailPopup);
